(() => {
  const elementName = "product-delivery-estimate";

  if (customElements.get(elementName)) return;

  const cacheKey = "filip:ipwhois-location:v3";
  let locationRequest;
  const regionNames = {
    acre: "AC",
    alagoas: "AL",
    amapa: "AP",
    amazonas: "AM",
    bahia: "BA",
    ceara: "CE",
    "distrito federal": "DF",
    "espirito santo": "ES",
    goias: "GO",
    maranhao: "MA",
    "mato grosso": "MT",
    "mato grosso do sul": "MS",
    "minas gerais": "MG",
    para: "PA",
    paraiba: "PB",
    parana: "PR",
    pernambuco: "PE",
    piaui: "PI",
    "rio de janeiro": "RJ",
    "rio grande do norte": "RN",
    "rio grande do sul": "RS",
    rondonia: "RO",
    roraima: "RR",
    "santa catarina": "SC",
    "sao paulo": "SP",
    sergipe: "SE",
    tocantins: "TO",
  };
  const regionDisplayNames = {
    AC: "Acre",
    AL: "Alagoas",
    AP: "Amapá",
    AM: "Amazonas",
    BA: "Bahia",
    CE: "Ceará",
    DF: "Distrito Federal",
    ES: "Espírito Santo",
    GO: "Goiás",
    MA: "Maranhão",
    MT: "Mato Grosso",
    MS: "Mato Grosso do Sul",
    MG: "Minas Gerais",
    PA: "Pará",
    PB: "Paraíba",
    PR: "Paraná",
    PE: "Pernambuco",
    PI: "Piauí",
    RJ: "Rio de Janeiro",
    RN: "Rio Grande do Norte",
    RS: "Rio Grande do Sul",
    RO: "Rondônia",
    RR: "Roraima",
    SC: "Santa Catarina",
    SP: "São Paulo",
    SE: "Sergipe",
    TO: "Tocantins",
  };

  const normalize = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const getCachedLocation = () => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey));
      return cached?.countryCode === "BR" &&
        typeof cached.region === "string" &&
        typeof cached.regionName === "string"
        ? cached
        : null;
    } catch (_error) {
      return null;
    }
  };

  const cacheLocation = (location) => {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(location));
    } catch (_error) {
      return;
    }
  };

  const resolveRegion = (response) => {
    const region = String(response.region_code || "").trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(region)) return region;
    return regionNames[normalize(response.region)] || "";
  };

  const requestLocation = () => {
    const cached = getCachedLocation();
    if (cached) return Promise.resolve(cached);
    if (locationRequest) return locationRequest;

    const request = (async () => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 5000);

      try {
        const url = new URL("https://ipwho.is/");
        url.search = new URLSearchParams({
          fields: "success,country_code,region,region_code",
        }).toString();

        const response = await fetch(url, {
          method: "GET",
          mode: "cors",
          credentials: "omit",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error("Location request failed");

        const payload = await response.json();
        const countryCode = String(payload.country_code || "").toUpperCase();
        const region = resolveRegion(payload);
        const regionName = regionDisplayNames[region];

        if (payload.success !== true || countryCode !== "BR" || !region || !regionName) {
          throw new Error("Location response is not usable");
        }

        const location = { countryCode, region, regionName };
        cacheLocation(location);
        return location;
      } finally {
        window.clearTimeout(timeout);
      }
    })();

    locationRequest = request.finally(() => {
      locationRequest = undefined;
    });
    return locationRequest;
  };

  class ProductDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      const message = this.querySelector("[data-delivery-estimate-message]");
      const daysData = this.querySelector("[data-delivery-estimate-days]");

      if (!message || !daysData) return;

      const fallbackMessage = message.textContent;
      const showFallback = () => {
        message.textContent = fallbackMessage;
        delete this.dataset.estimatedRegion;
        this.dataset.estimateSource = "fallback";
      };

      let daysByRegion;
      try {
        daysByRegion = JSON.parse(daysData.textContent);
      } catch (_error) {
        showFallback();
        return;
      }

      requestLocation()
        .then(({ region, regionName }) => {
          const days = Number(daysByRegion[region]);
          if (!Number.isInteger(days) || days < 1) {
            showFallback();
            return;
          }

          const prefix = message.dataset.regionTextPrefix;
          const suffix = days === 1 ? message.dataset.regionTextOneSuffix : message.dataset.regionTextOtherSuffix;
          if (!prefix || !suffix) {
            showFallback();
            return;
          }

          const regionElement = document.createElement("strong");
          regionElement.textContent = regionName;
          message.replaceChildren(
            document.createTextNode(prefix),
            regionElement,
            document.createTextNode(suffix.replace("__DAYS__", String(days)))
          );
          this.dataset.estimatedRegion = region;
          this.dataset.estimateSource = "ipwho-is";
        })
        .catch(showFallback);
    }
  }

  customElements.define(elementName, ProductDeliveryEstimate);
})();
